import Stars, {StarIcon} from '../components/Stars';
import {FulfillmentText} from '../components/inventory/InventoryTerms';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, LogOut, Heart, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getProducts, getUserOrders } from '../api';
import { useFavorites } from '../context/FavoritesContext';
import { orderStatuses } from '../data/constants';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartSidebar from '../components/CartSidebar';
import LoginModal from '../components/LoginModal';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ProfilePage = () => {
  const { user, logout, setIsLoginModalOpen, refreshUser } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadOrders();
      refreshUser();
      getProducts().then(setProducts).catch(error => console.error('Failed to load products:', error)).finally(() => setFavoritesLoading(false));
    }
  }, [user?.id]);

  const loadOrders = async () => {
    if (!user) return;
    try {
      const data = await getUserOrders(user.id);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
        <Header />
        <LoginModal />
        <main className="pt-24 pb-24">
          <div className="max-w-[1920px] mx-auto px-4 md:px-8 lg:px-12">
            <div className="text-center py-16">
              <h1 className="text-2xl font-medium mb-4 text-brand-text dark:text-brand-text">Личный кабинет</h1>
              <p className="text-brand-text dark:text-brand-text mb-6">Войдите, чтобы просматривать ваши заказы и старсы</p>
              <Button
                onClick={() => setIsLoginModalOpen(true)}
                className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text hover:bg-brand-surface dark:hover:bg-brand-surface"
              >
                Войти
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const currentOrder = orders.find(o => o.status === 'processing' || o.status === 'shipping' || o.status === 'new');

  return (
    <div className="min-h-screen bg-brand-surface dark:bg-[#1E1E1E] transition-colors">
      <Header />
      <CartSidebar />

      <main className="pt-20 md:pt-24 pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl md:text-3xl font-medium text-brand-text dark:text-brand-text">Личный кабинет</h1>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center gap-2 dark:border-brand-border dark:text-brand-text"
            >
              <LogOut className="w-4 h-4" />
              Выйти
            </Button>
          </div>

          {/* User Info */}
          <div className="bg-brand-surface dark:bg-brand-surface rounded-lg p-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-brand-surface dark:bg-brand-surface rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-brand-text dark:text-brand-text" />
              </div>
              <div>
                <h2 className="font-medium text-lg text-brand-text dark:text-brand-text">{user.name || 'Пользователь'}</h2>
                <p className="text-brand-text dark:text-brand-text">{user.email}</p>
                {user.phone && <p className="text-brand-text dark:text-brand-text">{user.phone}</p>}
              </div>
            </div>
          </div>

          {/* Coins Balance */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 border border-brand-border dark:border-brand-border rounded-lg p-6 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <StarIcon className="w-8 h-8" />
                <div>
                  <p className="text-sm text-brand-text dark:text-brand-text">Ваш баланс</p>
                  <p className="text-2xl font-bold text-brand-text dark:text-brand-text"><Stars value={user.coins} /></p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-brand-text dark:text-brand-text">Можно списать до 15%</p>
                <p className="text-xs text-brand-text dark:text-brand-text">от суммы заказа</p>
              </div>
            </div>
          </div>

          {/* Current Order Alert */}
          {currentOrder && (
            <div className="bg-brand-surface dark:bg-brand-accent/20 border border-brand-border dark:border-brand-border rounded-lg p-6 mb-8">
              <div className="flex items-center gap-3">
                <Package className="w-6 h-6 text-brand-text" />
                <div>
                  <p className="font-medium text-brand-text dark:text-brand-text">Текущий заказ #{currentOrder.id}</p>
                  <p className="text-sm text-brand-text dark:text-brand-text">
                    Статус: <span className="font-medium">{orderStatuses[currentOrder.status] || currentOrder.status}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabs */}
          <Tabs defaultValue="orders" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 dark:bg-brand-surface">
              <TabsTrigger value="orders" className="flex items-center gap-2 dark:data-[state=active]:bg-brand-surface">
                <Package className="w-4 h-4" />
                Заказы
              </TabsTrigger>
              <TabsTrigger value="favorites" className="flex items-center gap-2 dark:data-[state=active]:bg-brand-surface">
                <Heart className="w-4 h-4" />
                Избранное
              </TabsTrigger>
            </TabsList>

            <TabsContent value="orders">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-text" />
                </div>
              ) : orders.length > 0 ? (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div key={order.id} className="border dark:border-brand-border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-medium text-brand-text dark:text-brand-text">Заказ #{order.id}</p>
                          <p className="text-sm text-brand-text dark:text-brand-text">
                            {new Date(order.created_at).toLocaleDateString('ru-RU')}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          order.status === 'delivered' ? 'bg-brand-surface dark:bg-brand-accent/30 text-brand-text dark:text-brand-text' :
                          order.status === 'cancelled' ? 'bg-brand-surface dark:bg-brand-accent/30 text-brand-text dark:text-brand-text' :
                          'bg-brand-surface dark:bg-brand-accent/30 text-brand-text dark:text-brand-text'
                        }`}>
                          {orderStatuses[order.status] || order.status}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {order.pickup_code && (
                          <div className="mb-3 rounded-lg bg-brand-surface dark:bg-brand-surface p-3">
                            <span className="text-sm text-brand-text dark:text-brand-text">Код самовывоза: </span>
                            <span className="font-mono font-bold tracking-widest text-brand-text dark:text-brand-text">{order.pickup_code}</span>
                            <span className="ml-2 text-xs text-brand-text dark:text-brand-text">{order.pickup_issued ? 'заказ выдан' : 'покажите менеджеру при получении'}</span>
                          </div>
                        )}
                        <FulfillmentText lines={order.fulfillment||[]}/>
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm text-brand-text dark:text-brand-text">
                            <span>{item.name} ({item.size}) x {item.quantity}</span>
                            <span>{(item.price * item.quantity).toLocaleString('ru-RU')} ₽</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t dark:border-brand-border mt-4 pt-4 flex justify-between">
                        <span className="font-medium text-brand-text dark:text-brand-text">Итого:</span>
                        <span className="font-medium text-brand-text dark:text-brand-text">{order.total?.toLocaleString('ru-RU')} ₽</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-brand-text dark:text-brand-text">У вас пока нет заказов</p>
                  <Link to="/" className="text-brand-text dark:text-brand-text underline mt-2 inline-block">
                    Перейти к товарам
                  </Link>
                </div>
              )}
            </TabsContent>

            <TabsContent value="favorites">
              {favoritesLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-brand-text" /></div>
              ) : products.filter(product => favorites.includes(product.id)).length ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {products.filter(product => favorites.includes(product.id)).map(product => <ProductCard key={product.id} product={product} />)}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-brand-text dark:text-brand-text mb-4">У вас пока нет избранных товаров</p>
                  <Link to="/"><Button className="bg-brand-accent dark:bg-brand-accent text-brand-text dark:text-brand-text">Перейти к товарам</Button></Link>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
